import { useMemo } from 'react'

import { Box, Card, Spinner, Text } from '@factorialco/deprecated-design-system'
import { ReviewEmployeeScoreInsightGroupsDocument } from 'generated/resources/graphql'
import { xor } from 'lodash'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { Filter } from 'modules/core/components/DataTable/types'
import { useBarChartFilters } from 'modules/core/hooks/useBarChartFilters'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SCORE_RANGE_FILTER_IDENTIFIER } from 'modules/performance_analytics/components/PerformanceInsightsSummaryDatatable/filters/scoreFilter'
import {
  ChartData,
  ReviewEmployeeScoreChart,
} from 'modules/performance_analytics/components/ReviewEmployeeScoreChart'
import {
  GroupByType,
  TOP_VALUES_LIMIT,
  mapGroupByValueToName,
} from 'modules/performance_analytics/components/helpers'
import { useGroupByKeysLookupTransformer } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'
import { colorsForEmployeeScoreScaleId } from 'modules/performance_process/lib/employeeScoreColor'

type Bucket =
  | 'needsImprovment'
  | 'roomForGrouth'
  | 'meetsExpectations'
  | 'exceedsExpectations'
  | 'outstanding'

interface Props {
  employeeTotalCount: number | null | undefined
  groupBy?: GroupByType
  showEnterpriseUpsellingKit: boolean
}

const BUCKET_ORDER: Bucket[] = [
  'needsImprovment',
  'roomForGrouth',
  'meetsExpectations',
  'exceedsExpectations',
  'outstanding',
]

type GroupDataType = {
  values: string[]
  count: number[]
} | null

interface GroupsDataType {
  needsImprovment: GroupDataType
  roomForGrouth: GroupDataType
  meetsExpectations: GroupDataType
  exceedsExpectations: GroupDataType
  outstanding: GroupDataType
}

function getBucketName(bucket: Bucket): string {
  switch (bucket) {
    case 'needsImprovment':
      return i18n.t(
        'performance_process.analytics.score_distribution_chart.buckets.needs_improvement'
      )
    case 'roomForGrouth':
      return i18n.t(
        'performance_process.analytics.score_distribution_chart.buckets.room_for_growth'
      )
    case 'meetsExpectations':
      return i18n.t(
        'performance_process.analytics.score_distribution_chart.buckets.meets_expectations'
      )
    case 'exceedsExpectations':
      return i18n.t(
        'performance_process.analytics.score_distribution_chart.buckets.exceeds_expectations'
      )
    case 'outstanding':
      return i18n.t('performance_process.analytics.score_distribution_chart.buckets.outstanding')
    default:
      return bucket
  }
}

// getGroupByData function and interface removed since new query structure provides data directly

type ChartDataWithDynamicKeys = ChartData & Record<string, string | number>

export const ReviewEmployeeScoreInsights = ({
  groupBy,
  employeeTotalCount,
  showEnterpriseUpsellingKit,
}: Props) => {
  const {
    getFiltersVariables,
    availableFilters,
    filters: enabledFilters,
    onAddFilter,
    onUpdateFilter,
  } = useFilters()
  const filter = useMemo(
    () => JSON.parse(JSON.stringify(getFiltersVariables().filter)) ?? {},
    [getFiltersVariables]
  )
  const dataTableFilters = useMemo(() => filter ?? {}, [filter])

  const { data: queryResponse, loading: queryLoading } = useQuery(
    ReviewEmployeeScoreInsightGroupsDocument,
    {
      variables: {
        groupBy: groupBy ?? '',
        employeeIds: dataTableFilters.employeeId?.in,
        managerIds: dataTableFilters.managerId?.in,
        teamIds: dataTableFilters.teamId?.in,
        tenureDate: dataTableFilters.tenureDate?.in,
        potentialScoreRange: dataTableFilters.potentialScoreRange?.in,
        genders: dataTableFilters.gender?.in,
        workspaceIds: dataTableFilters.workspace?.in,
        legalEntityIds: dataTableFilters.legalEntityId?.in,
        nationalities: dataTableFilters.nationality?.in,
        pulseValues: dataTableFilters.pulseAnswer?.in?.map((x: string): number => Number(x)),
        projectIds: dataTableFilters.project?.in,
        gridStatus: dataTableFilters.gridStatus?.in,
        includeGroups: !!groupBy,
        singleValueInsight: !!!groupBy,
        groupsFilters: dataTableFilters,
      },
    }
  )

  const data = queryResponse
  const getGroupData = (groupName: Bucket) => {
    if (!groupBy || !data) {
      return {
        values: [],
        count: [],
      }
    }

    const bucketNode =
      data?.performance?.employeePerformanceInsightsSummaryGroupsConnection?.nodes?.find(
        (node) => node?.bucket === groupName
      )

    if (!bucketNode) {
      return {
        values: [],
        count: [],
      }
    }

    return {
      values: bucketNode.groups ?? [],
      count: bucketNode.counts ?? [],
    }
  }
  const getAllUniqueValuesFromBuckets = (groupsData: GroupsDataType): Set<string> => {
    const uniqueValues = new Set<string>()

    if (!groupBy) {
      return uniqueValues
    }

    Object.values(groupsData).forEach((bucketData) => {
      if (bucketData && bucketData.values.length > 0) {
        bucketData.values.forEach((value: string) => {
          uniqueValues.add(value)
        })
      }
    })

    return uniqueValues
  }
  const groupsData: GroupsDataType = {
    needsImprovment: getGroupData('needsImprovment'),
    roomForGrouth: getGroupData('roomForGrouth'),
    meetsExpectations: getGroupData('meetsExpectations'),
    exceedsExpectations: getGroupData('exceedsExpectations'),
    outstanding: getGroupData('outstanding'),
  }

  const allUniqueKeysWhenGroupBy = getAllUniqueValuesFromBuckets(groupsData)
  const {
    legalEntities,
    locations,
    employees: employeesNames,
    teams,
    loading: loadingLookups,
  } = useGroupByKeysLookupTransformer(groupBy, Array.from(allUniqueKeysWhenGroupBy))

  const categories =
    data?.performance?.reviewEmployeeScoreInsightsConnection?.edges[0]?.node?.categories || []
  const series =
    data?.performance?.reviewEmployeeScoreInsightsConnection?.edges[0]?.node?.series || []
  const colors = colorsForEmployeeScoreScaleId(1)

  const regularChartData = categories.map((bucket, index) => {
    return {
      bucket,
      score: series[index] ?? 0,
      color: colors[index] ?? 'grey400',
    }
  })

  const stackedChartData: ChartDataWithDynamicKeys[] = []
  const allUniqueValuesSet = new Set<string>()
  const showGroupBy = !!(groupBy && data)

  if (showGroupBy) {
    BUCKET_ORDER.forEach((bucketKey) => {
      const displayName = getBucketName(bucketKey)
      const bucketData = groupsData[bucketKey]

      const result: ChartDataWithDynamicKeys = {
        bucket: displayName,
        color: colors[BUCKET_ORDER.indexOf(bucketKey)] ?? 'grey400',
      }

      if (bucketData && bucketData.values.length > 0) {
        const valueCounts = bucketData.values.map((value, index) => ({
          value,
          count: Number(bucketData.count[index] ?? 0),
        }))

        valueCounts.sort((a, b) => b.count - a.count)

        const topValues = valueCounts.slice(0, TOP_VALUES_LIMIT)
        const otherValues = valueCounts.slice(TOP_VALUES_LIMIT)
        let otherCount = 0

        otherValues.forEach((item) => {
          otherCount += item.count
        })

        topValues.forEach((item) => {
          const formattedValue = mapGroupByValueToName(
            item.value,
            groupBy,
            legalEntities,
            teams,
            locations,
            employeesNames
          )
          result[formattedValue] = item.count
          allUniqueValuesSet.add(formattedValue)
        })

        if (valueCounts.length > TOP_VALUES_LIMIT && otherCount > 0) {
          result['Other'] = otherCount
          allUniqueValuesSet.add('Other')
        }
      }

      stackedChartData.push(result)
    })
  }

  const barChartConfig = useMemo(
    () => ({
      enabledFilters,
      availableFilters,
      dataTableFilters,
      scoreFilterIdentifier: SCORE_RANGE_FILTER_IDENTIFIER,
    }),
    [enabledFilters, availableFilters, dataTableFilters]
  )

  const filteredRegularChartData = useBarChartFilters(regularChartData, barChartConfig)
  const filteredStackedChartData = useBarChartFilters(stackedChartData, barChartConfig)

  const chartData = showGroupBy ? filteredStackedChartData : filteredRegularChartData

  if (!data || loadingLookups || queryLoading) {
    return (
      <Box height="s400" width="full">
        <Card>
          <Card.Content>
            <Box alignSelf="center" flexDirection="column">
              <Box paddingTop="s140">
                <Spinner />
              </Box>
            </Box>
          </Card.Content>
        </Card>
      </Box>
    )
  }

  const uniqueGroupValues = Array.from(allUniqueValuesSet)

  return (
    <ReviewEmployeeScoreChart
      data={chartData}
      hasRecords={!!data.performance.total?.totalCount}
      showGroupBy={showGroupBy ?? false}
      groupBy={groupBy}
      groupKeys={showGroupBy ? uniqueGroupValues : undefined}
      total={employeeTotalCount}
      loading={!data}
      showEnterpriseUpsellingKit={showEnterpriseUpsellingKit}
      tooltip={i18n.t('performance_process.analytics.score_distribution_chart.tooltip')}
      margin={
        showGroupBy
          ? {
              top: 35,
              bottom: 140,
              left: 30,
              right: -20,
            }
          : undefined
      }
      onBarClick={(datum) => {
        const { scoreFilter, selectedOption, filterAlreadyEnabled } =
          getSelectedFilterAndOptionByIndex(datum.index + 1, enabledFilters, availableFilters)

        if (!scoreFilter || !selectedOption) {
          return
        }

        track(trackingEvents.performanceAnalytics.insightsSummaryScoreBarClicked, {
          category: datum.label,
        })

        const currentValue = dataTableFilters.scoreRange?.in || []
        // Toggle the selected score range: add if not present, remove if already selected
        const nextValue = xor(currentValue, [selectedOption.value])
        const nextFilter = { ...scoreFilter, value: nextValue }

        // Prevent dropdown auto-opening when applying filter programmatically
        // Example: when user clicks chart bar instead of filter dropdown directly
        nextFilter.noAutoOpen = true

        if (filterAlreadyEnabled) {
          onUpdateFilter(nextFilter)
          return
        }

        onAddFilter(nextFilter)
      }}
    >
      <Box paddingBottom="s8">
        <Text.Normal color="textSecondary">
          {employeeTotalCount
            ? i18n.t('performance_process.analytics.score_distribution_chart.description', {
                employees_count: employeeTotalCount,
              })
            : ''}
        </Text.Normal>
      </Box>
    </ReviewEmployeeScoreChart>
  )
}

function hasCategory(obj: unknown): obj is { category?: { options?: Array<{ value: unknown }> } } {
  return Boolean(obj && typeof obj === 'object' && 'category' in obj)
}

function isValidOption(option: unknown): option is { value: unknown } {
  return Boolean(option && typeof option === 'object' && 'value' in option)
}

function getSelectedFilterAndOptionByIndex(
  index: number,
  enabledFilters: Filter<unknown>[],
  availableFilters: Filter<unknown>[]
) {
  const scoreRangeFinder = (filter: Filter<unknown>) =>
    filter.filter.identifier === SCORE_RANGE_FILTER_IDENTIFIER

  const scoreFilterAvailable: Filter<unknown> | undefined = availableFilters.find(scoreRangeFinder)
  const scoreFilterEnabled: Filter<unknown> | undefined = enabledFilters.find(scoreRangeFinder)
  const scoreFilter = scoreFilterAvailable || scoreFilterEnabled

  const parsedScoreFilter = scoreFilter as unknown
  const option = hasCategory(parsedScoreFilter)
    ? parsedScoreFilter.category?.options?.[index]
    : undefined

  const selectedOption = isValidOption(option) ? option : undefined
  return { scoreFilter, selectedOption, filterAlreadyEnabled: !!scoreFilterEnabled }
}
